import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, Input, OnInit, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { BROWSER_TYPE, INSPECTION_REPORT_STATUS_CODE } from '../../../../app.constants';
import { ApplicationError } from '../../../../_errors/application-error';
import { GeneratedReport, InspectionReport, PastReportVersion } from '../../../../_objects/inspection-report';
import { CommonSnackBarService } from '../../../../_services/common-snack-bar.service';
import { InspectionReportService } from '../../../../_services/http-api/inspection-report.service';
import { LangUpdateService } from '../../../../_services/lang-update.service';
import { SelectReportService } from '../../../../_services/select-report.service';
import { UtilsService } from '../../../../_services/_utils.service';
import { InspectionDataManageService, ViewReport } from '../../../inspection-data-manage.service';

@Component({
    selector: 'app-inspection-report-version',
    templateUrl: './inspection-report-version.component.html',
    styleUrls: ['./inspection-report-version.component.scss'],
    standalone: false
})
export class InspectionReportVersionComponent implements OnInit, OnChanges, OnDestroy {

  @Input() generatedReports: GeneratedReport[];
  @Input() targetReport: InspectionReport;
  @Input() isDeletedInspection: boolean;

  private  browserName: string;
  private onDestroy$ = new Subject();
  public nestedTreeControl: NestedTreeControl<GeneratedReport>;
  public nestedDataSource: MatTreeNestedDataSource<GeneratedReport>;
  public viewNode = (_: number, generatedReport: GeneratedReport) => true;
  private _getChildren = (node: GeneratedReport) => node.revisions;

  constructor(
    private router: Router,
    private utilsService: UtilsService,
    private snackBar: CommonSnackBarService,
    private inspectionReportService: InspectionReportService,
    private inspDataManageService: InspectionDataManageService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private selectReportService: SelectReportService
  ) {
    this.langService.prepare(translate, this.onDestroy$);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.generatedReports) {
      this.nestedDataSource = new MatTreeNestedDataSource();
      this.nestedDataSource.data = this.generatedReports;

      this.nestedTreeControl = new NestedTreeControl<GeneratedReport>(this._getChildren);
      this.nestedTreeControl.dataNodes = this.generatedReports;
      this.selectReportService.setViewReport(this.targetReport?.reportId, this.targetReport?.version);

      if (this.generatedReports) {
        const target = this.generatedReports.find(
          report => report.reportId === this.targetReport?.reportId);

        if (target) {
          this.nestedTreeControl.expand(target);
        }
      }
    }
  }

  ngOnInit() {
    this.browserName = this.utilsService.getBrowserName();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  /** inspection-report.component.tsのgetInspectionReportPdf()メソッドの呼び出し
   * @param  {number} report バージョン
   */
  download(report: PastReportVersion) {
    if (!this.targetReport || this.utilsService.isNullOrUndefined(this.targetReport.reportId)) {
      throw new ApplicationError('reportId is null.');
    }

    if (report.version === this.targetReport.version && this.isReportStatusEditing()) {
      return;
    }

    // pop up block対応のため、一旦空のタブを表示させる
    if (this.browserName === BROWSER_TYPE.SAFARI) {
      window.open('pdf-load', 'child' + this.targetReport.reportId + '_' + report.version);
    }

    // Chromeの場合はgetInspectionReportPdfでdownloadさせる
    this.inspectionReportService
      .getInspectionReportPdf(this.targetReport.reportId, report.version)
      .subscribe(
        blob => {
          this.utilsService.download(blob, report.title + '.pdf', this.targetReport.reportId);
        },
        error => {
          console.log(error);
          this.snackBar.open(this.translate.instant('inspectionReportVersion.downloadError'));
        }
      );
  }

  /** レポートが編集中であるか判別する
   * @returns boolean
   */
  isReportStatusEditing(): boolean {
    return this.utilsService.isReportStatusEditing(this.targetReport.status)
      || this.utilsService.isReportStatusFailed(this.targetReport.status);
  }

  isSelected(nestedNode: GeneratedReport) {
    return this.targetReport
      && this.targetReport.reportId === nestedNode.reportId
      && this.targetReport.version === nestedNode.version;
  }

  isSelectedParent(nestedNode: GeneratedReport) {
    if (!this.targetReport || !nestedNode) { return false; }

    if (nestedNode.isParent) {
      return this.targetReport.reportId === nestedNode.reportId
        && !this.nestedTreeControl.isExpanded(nestedNode);
    } else {
      return this.isSelected(nestedNode);
    }
  }

  isLatestVersion(version: number, reportId: string) {
    const latestVersion = this.generatedReports
      .filter(_ => _.reportId === reportId)
      .map(_ => _.version)
      .reduce((a, b) => Math.max(a, b));

    return version >= latestVersion;
  }

  hasNestedChild(generatedReport: GeneratedReport): boolean {
    return generatedReport.revisions?.length > 0;
  }

  clickNode(nestedNode: GeneratedReport) {
    const viewReport = new ViewReport(nestedNode.reportId, nestedNode.version);
    this.selectReportService.setViewReport(viewReport.reportId, viewReport.version);

    if (nestedNode.isParent) {
      if (!this.isDeletedInspection) {
        // Change report information only
        this.inspDataManageService.changeReportEmit(viewReport);
        this.nestedTreeControl.expand(nestedNode);
      }
    } else {
      // Report information & display the report
      this.inspDataManageService.viewReportEmit(viewReport);
    }
  }

  isEditingIcon(nestedNode): boolean {
    return this.isLatestVersion(nestedNode.version, nestedNode.reportId)
      && (nestedNode.status === INSPECTION_REPORT_STATUS_CODE.EDITING
        || nestedNode.status === INSPECTION_REPORT_STATUS_CODE.DRAFT_PROCESSING
        || nestedNode.status === INSPECTION_REPORT_STATUS_CODE.FINAL_PROCESSING
        || nestedNode.status === INSPECTION_REPORT_STATUS_CODE.FAILED);
  }
}
